import { useState, useRef, useCallback } from "react";
import axios from "axios";
import { API, authHeaders, errMsg } from "@/lib/galleryApi";
import { makeUploadVariants } from "@/lib/photoEngine";

const CONCURRENCY = 3;
const ACCEPTED = /\.(jpe?g|png|webp)$/i;

// Bulk import: thumbnails/previews generated in the browser, 3 parallel uploads, retry of failures
export function useGalleryUpload(galleryId, onPhoto) {
  const [state, setState] = useState({ running: false, total: 0, done: 0, failed: [], skipped: 0 });
  const cancelRef = useRef(false);

  const uploadOne = async (file) => {
    const form = new FormData();
    form.append("file", file);
    form.append("upload_key", `${file.name}|${file.size}|${file.lastModified}`);
    try {
      const v = await makeUploadVariants(file);
      form.append("thumb", v.thumb, "thumb.webp");
      form.append("preview", v.preview, "preview.jpg");
      form.append("width", v.width);
      form.append("height", v.height);
    } catch {
      // server generates variants as a fallback
    }
    // automatic retries on network interruption; the upload key makes retries safe (no duplicates)
    for (let attempt = 0; ; attempt++) {
      try {
        const res = await axios.post(`${API}/api/galleries/${galleryId}/photos/upload`, form, { headers: authHeaders(), timeout: 180000 });
        return res.data;
      } catch (e) {
        const network = !e.response || e.response.status >= 500;
        if (!network || attempt >= 5 || cancelRef.current) {
          if (!e.response) e.message = "Connexion interrompue";
          throw e;
        }
        // wait for the connection to come back (max 60s), then back off
        if (!navigator.onLine) {
          await new Promise((r) => {
            const t = setTimeout(r, 60000);
            window.addEventListener("online", () => { clearTimeout(t); r(); }, { once: true });
          });
        }
        await new Promise((r) => setTimeout(r, Math.min(8000, 1000 * 2 ** attempt)));
      }
    }
  };

  const start = useCallback(async (fileList) => {
    const files = Array.from(fileList || []);
    const rejected = files.filter((f) => !ACCEPTED.test(f.name)).map((f) => ({ name: f.name, error: "Format non pris en charge (JPEG, PNG, WebP uniquement — RAW/HEIC : moteur specialise requis)" }));
    const queue = files.filter((f) => ACCEPTED.test(f.name));
    cancelRef.current = false;
    setState({ running: true, total: queue.length, done: 0, failed: rejected, skipped: 0 });
    const failed = [...rejected];
    let idx = 0;
    const worker = async () => {
      while (idx < queue.length && !cancelRef.current) {
        const file = queue[idx++];
        try {
          const p = await uploadOne(file);
          if (p.duplicate) setState((st) => ({ ...st, skipped: st.skipped + 1 }));
          else onPhoto(p);
        } catch (e) {
          failed.push({ name: file.name, file, error: errMsg(e, "Echec de l'envoi") });
        }
        setState((s) => ({ ...s, done: s.done + 1, failed: [...failed] }));
      }
    };
    await Promise.all(Array.from({ length: CONCURRENCY }, worker));
    setState((s) => ({ ...s, running: false, failed: [...failed] }));
    return { ok: queue.length - failed.length + rejected.length, failed };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [galleryId, onPhoto]);

  const retryFailed = () => start(state.failed.filter((f) => f.file).map((f) => f.file));
  const cancel = () => { cancelRef.current = true; };
  const clear = () => setState({ running: false, total: 0, done: 0, failed: [], skipped: 0 });

  return { ...state, start, retryFailed, cancel, clear };
}
